import { AppstoreAddOutlined, ClearOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, Space, Tooltip } from 'antd'
import React, { useRef } from 'react'
import Highlighter from 'react-highlight-words'

import { sortDates } from '../../util/sorters/sort-dates'
import { sortNumbers } from '../../util/sorters/sort-numbers'
import { sortStrings } from '../../util/sorters/sort-strings'

interface Props {
  dataIndex: string
}

export const sortStringColumn =
  ({ dataIndex }: Props) =>
  (previous, next) =>
    sortStrings(String(previous[dataIndex]), String(next[dataIndex]))

export const sortNumericColumn =
  ({ dataIndex }: Props) =>
  (previous, next) =>
    sortNumbers(previous[dataIndex], next[dataIndex])

export const sortDateColumn =
  ({ dataIndex }: Props) =>
  (previous, next) =>
    sortDates(new Date(previous[dataIndex]), new Date(next[dataIndex]))

export const sortBooleanColumn =
  ({ dataIndex }: Props) =>
  (previous, next) =>
    previous[dataIndex] < next[dataIndex] ? -1 : 1

export const renderTableActions = ({
  id,
  onDetail,
  onUpdate,
  onRemove,
  onExtra,
  extraLabel,
}: {
  id: number
  onDetail: (objectId) => void
  onUpdate?: (objectId) => void
  onRemove?: (objectId) => void
  onExtra?: (objectId) => void
  extraLabel?: string
}) => (
  <Space>
    <Tooltip title="Details">
      <Button shape="circle" icon={<SearchOutlined />} onClick={() => onDetail(id)} />
    </Tooltip>
    {onExtra && (
      <Tooltip title={extraLabel}>
        <Button shape="circle" icon={<AppstoreAddOutlined />} onClick={() => onExtra(id)} />
      </Tooltip>
    )}
    {onRemove && (
      <Tooltip title="Remove">
        <Button shape="circle" icon={<PlusOutlined />} onClick={() => onRemove(id)} />
      </Tooltip>
    )}
    {onUpdate && (
      <Tooltip title="Update">
        <Button shape="circle" icon={<EditOutlined />} onClick={() => onUpdate(id)} />
      </Tooltip>
    )}
  </Space>
)

export const buildSearchFilterProps = ({ dataIndex, render }: { dataIndex: string; render?: any }) => {
  let searchInput: any = null
  let searchText = ''
  let searchedColumn = ''

  const handleSearch = (selectedKeys: any, confirm: () => void, currentDataIndex: string) => {
    confirm()
    const [text] = selectedKeys
    searchText = text
    searchedColumn = currentDataIndex
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    searchText = ''
  }

  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div className="table-search">
        <Input
          ref={(node) => {
            searchInput = node
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          autoFocus
        />
        <Space>
          <Button type="link" onClick={() => handleReset(clearFilters)} icon={<ClearOutlined />} size="small">
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Search
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value: any, record: any) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
    onFilterDropdownVisibleChange: (visible: boolean) => visible && (() => searchInput.select()),
    render: (text: string, ...rest) => {
      const result =
        searchedColumn === text ? (
          <Highlighter
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
            className="table-search-highlight"
          />
        ) : (
          text
        )

      return render ? render(result, ...rest) : result
    },
  }
}
